/**
 * 69.29 step 3b (DECISIONS 245 (4), the owner: "yep let's do v for aim"): the tank gunner's sight — V in the turret's seat
 * looks through the gun at a quarter of the usual field, a reticle at its centre with the explosive round's drop marks to its
 * range, and the gun's state (the round in it, the round next, the stowage). Presentation only: the host places the camera,
 * this draws the overlay; the marks' angles are a pure function of the round (its speed, its share of the felt gravity).
 */
import type { TankRound } from '../types/body';

/** The sight's field as a share of the usual view's. */
export const GUNSIGHT_FOV_SHARE = 0.25;
/** The ranges (people) the reticle marks the explosive round's drop at. */
export const GUNSIGHT_RANGES = [100, 200, 300] as const;

/**
 * Each range's mark below the reticle's centre (pixels): the round's fall over its flight there (no drag — the tank's rounds
 * have none) as an angle under the line, at a field of `fovDeg` degrees (vertical) on a view `height` pixels tall.
 */
export function dropMarks(speed: number, gravity: number, person: number, fovDeg: number, height: number, ranges: readonly number[] = GUNSIGHT_RANGES): Array<{ people: number; px: number }> {
  const half = Math.tan((fovDeg * Math.PI) / 360);
  return ranges.map(people => {
    const d = people * person, t = d / speed, fall = 0.5 * gravity * t * t;
    return { people, px: (fall / d / half) * (height / 2) };
  });
}

/** 69.54: with the seconds a reload has left, and the range (people) to what the aim rests on (null: nothing in reach). */
export interface GunsightState { round: TankRound | null; next: TankRound; he: number; solid: number; reloading: boolean; reloadLeft?: number; range?: number | null; marks: Array<{ people: number; px: number }> }

const WORD: Record<TankRound, string> = { he: 'HE', solid: 'SOLID' };

/** The overlay (made on first use, kept on the page): shown with the gun's state, or hidden with null. */
export function drawGunsight(el: HTMLDivElement | null, s: GunsightState | null): HTMLDivElement | null {
  if (!s) { if (el) el.style.display = 'none'; return el; }
  const div = el ?? makeSight();
  div.style.display = 'block';
  const marks = s.marks.map(m => `<div class="gs-mark" style="top:calc(50% + ${m.px.toFixed(1)}px)"><span>${m.people}</span></div>`).join('');
  const loaded = s.reloading ? `loading ${WORD[s.next]}${s.reloadLeft !== undefined ? ` ${s.reloadLeft.toFixed(1)} s` : ''}` : s.round ? `${WORD[s.round]} ready` : 'empty';
  (div.querySelector('.gs-marks') as HTMLDivElement).innerHTML = marks;
  (div.querySelector('.gs-state') as HTMLDivElement).textContent = `${loaded} · next ${WORD[s.next]} · HE ${s.he} · SOLID ${s.solid}`;
  (div.querySelector('.gs-range') as HTMLDivElement).textContent = s.range === undefined ? '' : s.range === null ? 'RANGE —' : `RANGE ${s.range}`;
  return div;
}

function makeSight(): HTMLDivElement {
  const div = document.createElement('div');
  div.id = 'tank-gunsight';
  div.innerHTML = `
    <style>
      #tank-gunsight { position: fixed; inset: 0; pointer-events: none; z-index: 41; font: 600 13px/1.3 system-ui, sans-serif; color: #d8f0c8; }
      #tank-gunsight .gs-ring { position: absolute; inset: 0; background: radial-gradient(circle at 50% 50%, transparent 0 38vmin, rgba(0,0,0,0.92) 39vmin); }
      #tank-gunsight .gs-h, #tank-gunsight .gs-v { position: absolute; background: rgba(216,240,200,0.9); }
      #tank-gunsight .gs-h { left: calc(50% - 30vmin); width: 60vmin; top: 50%; height: 1px; clip-path: polygon(0 0,46% 0,46% 100%,0 100%,0 0,54% 0,100% 0,100% 100%,54% 100%); }
      #tank-gunsight .gs-v { top: 50%; height: 30vmin; left: 50%; width: 1px; }
      #tank-gunsight .gs-mark { position: absolute; left: calc(50% - 2.2vmin); width: 4.4vmin; height: 1px; background: rgba(216,240,200,0.9); }
      #tank-gunsight .gs-mark span { position: absolute; left: 5vmin; top: -0.7em; font-size: 11px; }
      #tank-gunsight .gs-state { position: absolute; left: 50%; bottom: 9vmin; transform: translateX(-50%); padding: 3px 8px; background: rgba(0,0,0,0.5); border-radius: 4px; }
      #tank-gunsight .gs-range { position: absolute; left: calc(50% + 8vmin); top: calc(50% - 2.4em); font-size: 14px; letter-spacing: 0.05em; }
    </style>
    <div class="gs-ring"></div><div class="gs-h"></div><div class="gs-v"></div><div class="gs-marks"></div><div class="gs-range"></div><div class="gs-state"></div>`;
  document.body.appendChild(div);
  return div;
}
